// Минимальный SSE-frame parser.
//
// Стрим читается через `fetch()` + `response.body.getReader()` (с
// `credentials: "include"` — auth по HttpOnly session-cookie), а SSE
// фреймы парсятся руками: `fetch`-стриминг даёт единый клиентский путь
// с остальными запросами вместо отдельного `EventSource`-объекта.
//
// Формат SSE:
//
//   data: <payload>\n
//   data: <continuation>\n
//   \n              <- разделитель event'ов (пустая строка)
//
//   : comment\n     <- комментарий (heartbeat, мы игнорим)
//   \n
//
// Подробности — RFC HTML Living Standard, секция Server-Sent Events.
// Мы намеренно поддерживаем только подмножество, которое отдаёт наш
// backend: `data:` для event'ов и `:` для heartbeat. `event:`/`id:`/
// `retry:` пока не используются; если backend начнёт их слать —
// расширим без слома контракта (текущие тесты их прокладывают).

/**
 * Накопитель текстовых чанков из ReadableStream → SSE-event'ов.
 *
 * Использование:
 *
 * ```ts
 * const parser = new SseParser();
 * for (const chunk of chunks) {
 *   for (const event of parser.feed(chunk)) {
 *     onMessage(event);
 *   }
 * }
 * ```
 *
 * `feed()` возвращает массив завершённых SSE-фреймов (split по
 * `\n\n`), оставляя неполный «хвост» в буфере до следующего вызова.
 * Comment-фреймы (`:` в первой строке всех data-line'ов) сюда не
 * попадают — отбрасываются внутри.
 */
export class SseParser {
  // Незавершённый «хвост» — то, что после последнего `\n\n` ещё не
  // имеет терминатора. На него потенциально приклеится следующий
  // chunk. Хранится как строка, а не как массив-байтов: TextDecoder
  // в `useNotificationStream` уже декодировал UTF-8.
  private buffer = "";

  /**
   * Принять очередной текстовый chunk, вернуть массив завершённых
   * `data:` payload'ов. Heartbeat-`:`-comment'ы отфильтрованы и в
   * результат не попадают.
   */
  public feed(chunk: string): string[] {
    this.buffer += chunk;
    // SSE-frame ограничивается `\n\n` (две LF). CR-варианты (RFC
    // допускает `\r\n` и `\r`) у нас не возникают: FastAPI пишет LF.
    const frames: string[] = [];
    let separatorIdx: number;
    while ((separatorIdx = this.buffer.indexOf("\n\n")) !== -1) {
      const rawFrame = this.buffer.slice(0, separatorIdx);
      this.buffer = this.buffer.slice(separatorIdx + 2);
      const payload = parseFrame(rawFrame);
      if (payload !== null) {
        frames.push(payload);
      }
    }
    return frames;
  }
}

/**
 * Разобрать один SSE-frame в payload.
 *
 * - Если frame — comment (`: ...`) → `null` (heartbeat, игнорим).
 * - Если frame содержит одну/несколько `data: <chunk>` строк → склеить
 *   их через `\n` (по RFC `data:` многострочный) и вернуть.
 * - Любые другие field'ы (`event:`/`id:`/`retry:`) сейчас игнорим;
 *   backend их не отдаёт, тестами зафиксировано в `sseParser.test.ts`.
 *
 * `null` означает «фрейм был, но содержательного payload'а в нём нет»
 * — caller сам решает, что делать (мы просто скипаем).
 */
function parseFrame(rawFrame: string): string | null {
  const dataLines: string[] = [];
  for (const line of rawFrame.split("\n")) {
    if (line === "") {
      continue;
    }
    if (line.startsWith(":")) {
      // comment-line. SSE-frame считается comment-only, если все его
      // содержательные строки начинаются с `:`. Backend наш всегда
      // шлёт чистые `: ping\n\n` — без mixin'а с data.
      continue;
    }
    if (line.startsWith("data:")) {
      // По RFC: всё после `data:` — payload, опциональный leading
      // пробел `data: foo` → `foo` (срезаем один space, если есть).
      const value = line.slice("data:".length);
      dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
    }
    // Прочее (`event:`, `id:`, `retry:`) — игнорим, backend не посылает.
  }
  if (dataLines.length === 0) {
    return null;
  }
  return dataLines.join("\n");
}
